import { Component, HostListener, ElementRef, inject } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';

import { DataService } from '../../../Services/Data Service/data.service';
import { AuthService } from '../../../Auth/auth.service';
import { Route, Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { NotificationService } from '../../../Services/Data Service/notification.service';
import { isAdminRole } from '../../../roles';
@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [NgIf,CommonModule,RouterModule],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent {

  private pollingInterval: any;

  isListVisible = false;
  expanded = false;
  loadingNotifications: boolean = false;

  constructor(
    private eRef: ElementRef,
    private auth: AuthService,
    private dataService: DataService,
    private notificationService: NotificationService,
    private router: Router,
  ) {}
  
  toggleCard(){
    this.isListVisible = !this.isListVisible;
  }

  expandConfig(){
    this.expanded = !this.expanded;
  }

  isNavbarExpanded = false;

  toggleCollapse(expanded: boolean) {
    this.isNavbarExpanded = expanded;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Check if the click is outside the component
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.isListVisible = false;
    }
  }


  currUser: any = null;

  roleBasedLink: string = '/';


  ngOnInit(){
    const role = localStorage.getItem('role'); // or use authService if you've exposed a method
    this.roleBasedLink = isAdminRole(role) ? '/dashboard' : '/registrationform';

    const token = this.auth.getAccessToken();
    const email = this.auth.getUserEmail();

    if (!token || !email) {
      console.error('User is not logged in or email/token is missing.');
    }

    // Consume shared current user (set by app init or after login). Initial null is expected
    // before AppComponent finishes getCurrentUser — never call logout on that emission.
    this.dataService.currentUser$.subscribe({
      next: (loggedInUser) => {
        if (!loggedInUser?.role) {
          this.currUser = null;
          return;
        }
        this.currUser = loggedInUser;
        this.loadNotifications(this.currUser.intUserPK);
      },
    });

    this.pollingInterval = setInterval(() => {
      if (this.currUser?.intUserPK) {
        this.loadNotifications(this.currUser.intUserPK);
      }
    }, 10000);

  }

  ngOnDestroy(): void {
    clearInterval(this.pollingInterval);
  }
  
  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString();
  }

// NOTIFICATIONS
  notifications: any;

  /** Optional: cache creator by adminId to avoid refetching within TTL (e.g. across polls). */
  private creatorCache = new Map<number, { user: any; expires: number }>();
  private readonly CREATOR_CACHE_TTL_MS = 60_000;

  loadNotifications(userId: number) {
    this.loadingNotifications = true;
    this.notificationService.getNotificationsForUser(userId).subscribe((data: any[]) => {
      const notifications = data.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      const now = Date.now();
      const uniqueAdminIds = [...new Set(notifications.map(n => n.adminId).filter(id => id != null))] as number[];

      const toFetch = uniqueAdminIds.filter(id => {
        const entry = this.creatorCache.get(id);
        return !entry || entry.expires < now;
      });
      const userMap = new Map<number, any>();
      uniqueAdminIds.forEach(id => {
        const entry = this.creatorCache.get(id);
        if (entry && entry.expires >= now) userMap.set(id, entry.user);
      });

      if (toFetch.length === 0) {
        this.applyCreatorsAndMessages(notifications, userMap);
        this.loadingNotifications = false;
        return;
      }

      const userRequests = toFetch.map(id => this.dataService.getUserByPK(id));
      forkJoin(userRequests).pipe(finalize(() => (this.loadingNotifications = false))).subscribe({
        next: (users) => {
          toFetch.forEach((id, i) => {
            const user = users[i];
            userMap.set(id, user);
            this.creatorCache.set(id, { user, expires: now + this.CREATOR_CACHE_TTL_MS });
          });
          this.applyCreatorsAndMessages(notifications, userMap);
        },
        error: (error) => {
          console.error('Error getting user data', error);
        }
      });
    }, error => {
      console.error('Error loading notifications', error);
      this.loadingNotifications = false;
    });
  }

  private applyCreatorsAndMessages(notifications: any[], userMap: Map<number, any>): void {
    this.notifications = notifications.map((noti) => {
      noti.creator = noti.adminId != null ? userMap.get(noti.adminId) ?? null : null;

      switch (noti.type) {
        case 'action_required':
          noti.message = 'Your form was sent back. Open your registration form to view and make requested changes.';
          break;
        case 'approved':
          noti.message = 'Your registration form has been approved! You are now an active vendor.';
          break;
        case 'declined':
          noti.message = 'We regret to inform you that your registration form has been declined.';
          break;
        default:
          noti.message = noti.message;
      }

      return noti;
    });
  }

  showNotifications = false;

  closeNotifications() {
    this.showNotifications = false;
  }
  
  toggleNotifications(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showNotifications = !this.showNotifications;


    if (this.showNotifications) {
      this.markAllAsRead();
      this.loadNotifications(this.currUser.intUserPK);
    }
  }

  hasUnreadNotifications(): boolean {
    return this.notifications?.some((n: { read: boolean; }) => n.read === false) ?? false;
  }

  markAllAsRead(): void {
    console.log('Marking all as read...');
    this.notificationService.markAllAsRead(this.currUser.intUserPK).subscribe(() => {
      this.notifications.forEach((n: { isRead: boolean; }) => n.isRead = true);
    });
  }
  
  timeAgo(dateString: string): string {
    const submittedDate = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - submittedDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
    let timeAgoText = 'Today';
    if (diffDays === 1) {
      timeAgoText = '1 day ago';
    } else if (diffDays > 1) {
      timeAgoText = `${diffDays} days ago`;
    }
  
    return `${timeAgoText}`;
  }
  
}
